import type { BrowserOptions } from "@sentry/astro";

// Shared Sentry values for the browser SDK and the Worker.
//
// The DSN is a PUBLIC value: it permits one operation — submitting an event —
// and grants no read access and no project administration.
// It is supplied at BUILD time, because this site is `output: "static"` and has
// no server render that could hand it to a page at request time.
//
// Keeping the read in one module, rather than once per SDK config, stops the
// browser and the Worker reporting to two different Sentry projects.
//
// This copy intentionally has no committed fallback: the old literal reported
// to the source site's project. Until PUBLIC_SENTRY_DSN points at the dedicated
// Proseafarers project, both SDKs stay inert instead of mixing the two sites.
export const SENTRY_DSN: string = (
  import.meta.env.PUBLIC_SENTRY_DSN ?? ""
).trim();

// Sentry's own default environment is "production". A developer running
// `astro build` locally would then file events against the same environment the
// live site alerts on, so default to "development" instead and let each deploy
// name itself: `PUBLIC_SENTRY_ENVIRONMENT` at build time for the browser,
// the `SENTRY_ENVIRONMENT` Worker var at runtime for the Worker.
export const DEFAULT_SENTRY_ENVIRONMENT = "development";

/**
 * Resolve the Sentry environment name from a build-time or runtime value.
 *
 * An unset, empty or whitespace-only value falls back to
 * {@link DEFAULT_SENTRY_ENVIRONMENT}, so a deploy that forgets to set the
 * variable is visibly "development" rather than silently "production".
 */
export function resolveSentryEnvironment(value?: string | null): string {
  return value?.trim() || DEFAULT_SENTRY_ENVIRONMENT;
}

// Sentry 10's default deny patterns. The Sentry 11 migration guide applies the
// same list to header names and to URL query parameter names to keep the
// Sentry 10 behaviour, and this file does the same.
const SENTRY_V10_DENY_PATTERNS = [
  "forwarded",
  "-ip",
  "remote-",
  "via",
  "-user",
];

/**
 * What both SDKs may attach to an event.
 *
 * Sentry 11 replaced `sendDefaultPii` with this option and made an unset value
 * collect user info, cookies, headers and bodies. Readers of this site are not
 * logged in and nothing here needs to identify them, so this keeps the
 * restrictive Sentry 10 baseline. Both halves must pass it: leaving it out of
 * either one silently widens what that half collects.
 *
 * The Worker reads it from plain JavaScript, so src/lib/sentry.test.ts also
 * checks it against the option type of @sentry/cloudflare.
 */
export const SENTRY_DATA_COLLECTION = {
  userInfo: false,
  cookies: false,
  httpHeaders: {
    request: { deny: SENTRY_V10_DENY_PATTERNS },
    response: { deny: SENTRY_V10_DENY_PATTERNS },
  },
  httpBodies: [],
  urlQueryParams: { deny: SENTRY_V10_DENY_PATTERNS },
  genAI: { inputs: false, outputs: false },
  databaseQueryData: false,
  queues: false,
  graphQL: { document: false, variables: false },
} satisfies NonNullable<BrowserOptions["dataCollection"]>;
